import { effect, Injectable, signal } from "@angular/core";
import { LineLiveState } from "../models/live-production.model";

const STORAGE_KEY = "live-production:prefs:v2";

interface Prefs {
  onlyDevices: boolean;
  hideInactive: boolean;
  autoTheme: boolean;
  /** ligneIds à masquer (filtre persistant utilisateur). */
  hiddenLigneIds: string[];
  /** Ordre custom des ligneIds (drag-drop utilisateur). Les ids non listés sont
   *  ajoutés à la fin par ordre alphabétique. */
  lineOrder: string[];
  /** ligneIds dont la card est déployée (pilotage machine inline). */
  expandedLigneIds: string[];
  /** États masqués (filtre fin par type d'état). */
  hiddenStates: LineLiveState[];
}

const DEFAULT_PREFS: Prefs = {
  onlyDevices: false,
  hideInactive: false,
  autoTheme: true,
  hiddenLigneIds: [],
  lineOrder: [],
  expandedLigneIds: [],
  hiddenStates: [],
};

/** Ordre d'affichage canonique des états dans le menu de filtres. */
export const ALL_LINE_STATES: LineLiveState[] = [
  "EN_PROD",
  "EN_TRANSITION",
  "EN_ATTENTE",
  "HORS_PLAGE",
  "INACTIF",
  "DEVICE_OFFLINE",
];

/** Libellés humains des états (pour l'UI du filtre). */
export const LINE_STATE_LABELS: Record<LineLiveState, string> = {
  EN_PROD: "En production",
  EN_TRANSITION: "En transition",
  EN_ATTENTE: "En attente",
  HORS_PLAGE: "Hors plage",
  INACTIF: "Inactif",
  DEVICE_OFFLINE: "Hors ligne",
};

/**
 * Préférences locales partagées entre widget compact et page détachée.
 * Persistées en localStorage (clé `live-production:prefs:v2`).
 */
@Injectable({ providedIn: "root" })
export class LiveProductionPrefsService {
  onlyDevices = signal(DEFAULT_PREFS.onlyDevices);
  hideInactive = signal(DEFAULT_PREFS.hideInactive);
  autoTheme = signal(DEFAULT_PREFS.autoTheme);
  /** Set des `ligneId` masquées. */
  hiddenLigneIds = signal<Set<string>>(new Set<string>());
  /** Ordre custom des `ligneId` (drag-drop). */
  lineOrder = signal<string[]>([]);
  /** Set des `ligneId` dont la card est actuellement déployée. */
  expandedLigneIds = signal<Set<string>>(new Set<string>());
  /** Set des états masqués (filtre par état). */
  hiddenStates = signal<Set<LineLiveState>>(new Set<LineLiveState>());

  constructor() {
    this.load();
    // Persiste à chaque changement
    effect(() => {
      const prefs: Prefs = {
        onlyDevices: this.onlyDevices(),
        hideInactive: this.hideInactive(),
        autoTheme: this.autoTheme(),
        hiddenLigneIds: [...this.hiddenLigneIds()],
        lineOrder: this.lineOrder(),
        expandedLigneIds: [...this.expandedLigneIds()],
        hiddenStates: [...this.hiddenStates()],
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
      } catch {
        // ignore quota errors
      }
    });
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as Partial<Prefs>;
      if (typeof p.onlyDevices === "boolean") this.onlyDevices.set(p.onlyDevices);
      if (typeof p.hideInactive === "boolean") this.hideInactive.set(p.hideInactive);
      if (typeof p.autoTheme === "boolean") this.autoTheme.set(p.autoTheme);
      if (Array.isArray(p.hiddenLigneIds)) {
        this.hiddenLigneIds.set(new Set(p.hiddenLigneIds));
      }
      if (Array.isArray(p.lineOrder)) {
        this.lineOrder.set(p.lineOrder);
      }
      if (Array.isArray(p.expandedLigneIds)) {
        this.expandedLigneIds.set(new Set(p.expandedLigneIds));
      }
      if (Array.isArray(p.hiddenStates)) {
        this.hiddenStates.set(new Set(p.hiddenStates));
      }
    } catch {
      // ignore parse errors
    }
  }

  toggleHidden(ligneId: string): void {
    this.hiddenLigneIds.update((set) => {
      const next = new Set(set);
      if (next.has(ligneId)) {
        next.delete(ligneId);
      } else {
        next.add(ligneId);
      }
      return next;
    });
  }

  isHidden(ligneId: string): boolean {
    return this.hiddenLigneIds().has(ligneId);
  }

  showAll(): void {
    this.hiddenLigneIds.set(new Set());
  }

  hideAll(ligneIds: string[]): void {
    this.hiddenLigneIds.set(new Set(ligneIds));
  }

  isExpanded(ligneId: string): boolean {
    return this.expandedLigneIds().has(ligneId);
  }

  toggleExpanded(ligneId: string): void {
    this.expandedLigneIds.update((set) => {
      const next = new Set(set);
      if (next.has(ligneId)) {
        next.delete(ligneId);
      } else {
        next.add(ligneId);
      }
      return next;
    });
  }

  collapseAll(): void {
    this.expandedLigneIds.set(new Set());
  }

  isStateHidden(state: LineLiveState): boolean {
    return this.hiddenStates().has(state);
  }

  toggleHiddenState(state: LineLiveState): void {
    this.hiddenStates.update((set) => {
      const next = new Set(set);
      if (next.has(state)) {
        next.delete(state);
      } else {
        next.add(state);
      }
      return next;
    });
  }

  showAllStates(): void {
    this.hiddenStates.set(new Set());
  }

  /** Met à jour l'ordre custom complet (après drag-drop par exemple). */
  setLineOrder(order: string[]): void {
    this.lineOrder.set([...order]);
  }

  /** Reset l'ordre custom (revient à l'ordre alphabétique par défaut). */
  resetLineOrder(): void {
    this.lineOrder.set([]);
  }

  /**
   * Trie une liste de lignes selon l'ordre custom utilisateur.
   * Les lignes inconnues (nouvelles) sont placées à la fin par ordre alphabétique.
   */
  sortByCustomOrder<T extends { ligneId: string; ligneDenomination: string }>(
    lignes: T[],
  ): T[] {
    const order = this.lineOrder();
    if (order.length === 0) {
      return [...lignes].sort((a, b) =>
        a.ligneDenomination.localeCompare(b.ligneDenomination),
      );
    }
    const orderIndex = new Map<string, number>();
    order.forEach((id, idx) => orderIndex.set(id, idx));

    const known: T[] = [];
    const unknown: T[] = [];
    for (const l of lignes) {
      if (orderIndex.has(l.ligneId)) known.push(l);
      else unknown.push(l);
    }
    known.sort(
      (a, b) =>
        (orderIndex.get(a.ligneId) ?? 0) - (orderIndex.get(b.ligneId) ?? 0),
    );
    unknown.sort((a, b) =>
      a.ligneDenomination.localeCompare(b.ligneDenomination),
    );
    return [...known, ...unknown];
  }
}
